/* eslint-disable @typescript-eslint/no-explicit-any */
import { Address } from '@src/main/modules/messages/domain/address/address.interface';
import { SCGroup } from '@src/main/modules/messages/domain/subject/subject.interface';
import FloatLabelInput from '@src/main/shared/ui/components/input/FloatLabelInput';
import { Checkbox } from 'primereact/checkbox';
import { FloatLabel } from 'primereact/floatlabel';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SubjectForm.module.scss';

interface SubjectFormProps {
  values: {
    description: string;
    visibleOnApp: boolean;
    addresses: Address[];
    scGroups?: SCGroup[];
  };
  availableAddresses: Address[];
  availableScGroups: string[];
  handleChange: (field: keyof Omit<SubjectFormProps['values'], 'scGroups'>, value: any) => void;
  handleAddressChange: (a: Address, e: any) => void;
  handleScGroupChange: (scGroup: string, checked: boolean) => void;
  handleScGroupPriorityChange: (scGroup: string, priority: number) => void;
  errors?: Record<string, string>;
}

const SubjectForm: FC<SubjectFormProps> = ({
  values,
  availableAddresses,
  availableScGroups,
  handleChange,
  handleAddressChange,
  handleScGroupChange,
  handleScGroupPriorityChange,
  errors,
}) => {
  const { t } = useTranslation();

  return (
    <form>
      <div className={styles.grid}>
        <FloatLabelInput
          label={t('messages.subjects.description')}
          value={values.description}
          onChange={(val) => handleChange('description', val)}
          error={errors?.description}
          className={`${styles.field} col-12`}
          inputClassName={styles.inputfield}
        />

        <div className={`${styles.field} col-12`}>
          <label className={styles.label}>{t('messages.subjects.visibleOnApp')}</label>
          <InputSwitch
            className={styles.switch}
            checked={values.visibleOnApp}
            onChange={(e) => handleChange('visibleOnApp', e.value)}
          />
        </div>

        <div className={`${styles.field} col-12 mt-4`}>
          <h4 className="mt-0">Grupos SC</h4>
          <div className="grid">
            {availableScGroups.map((scGroup) => {
              const selectedGroup = values.scGroups?.find((sg) => sg.scGroup === scGroup);
              const isSelected = !!selectedGroup;
              const priority = selectedGroup?.priority ?? 1;

              return (
                <div
                  className="col-12"
                  key={scGroup}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    marginBottom: '1.5rem',
                    padding: '0.5rem 0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Checkbox
                      inputId={`cb-${scGroup}`}
                      value={scGroup}
                      checked={isSelected}
                      onChange={(e) => handleScGroupChange(scGroup, !!e.checked)}
                    />
                    <label htmlFor={`cb-${scGroup}`}>{scGroup}</label>
                  </div>

                  {isSelected && (
                    <div
                      style={{
                        marginLeft: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                      }}
                    >
                      <FloatLabel>
                        <InputNumber
                          value={priority}
                          onValueChange={(e) => handleScGroupPriorityChange(scGroup, e.value ?? 1)}
                          min={1}
                          showButtons
                          buttonLayout="stacked"
                          step={1}
                          className="custom-inputnumber"
                        />
                        <label htmlFor="number-input">Priority</label>
                      </FloatLabel>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className={`${styles.field} col-12 mt-4`}>
          <h4 className="mt-0">{t('messages.subjects.addresses')}</h4>
          <div className="grid">
            {availableAddresses.map((a) => (
              <div className="field-checkbox col-12" key={a.addressId}>
                <Checkbox
                  inputId={`cb-${a.addressId}`}
                  value={a.addressId}
                  onChange={(e) => handleAddressChange(a, e)}
                  checked={values.addresses.some((addr) => addr.addressId === a.addressId)}
                />
                <label htmlFor={`cb-${a.addressId}`}>{a.email}</label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </form>
  );
};

export default SubjectForm;
